import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { createPortal } from "react-dom";
import {
  Home, ListChecks, TrendingUp, MessageCircle, Sparkles,
  Users, Mic, Film, Target, Moon, Wind,
} from "lucide-react";

const SOLO = [
  { to: "/app", label: "Home", icon: Home },
  { to: "/routine", label: "Routine", icon: ListChecks },
  { to: "/progress", label: "Evolution", icon: TrendingUp },
];

const GROUPS = {
  coach: {
    label: "Coach",
    icon: MessageCircle,
    match: ["/coach", "/practice", "/talk"],
    items: [
      { to: "/coach", label: "Coach", desc: "Live drills & feedback", icon: MessageCircle },
      { to: "/practice", label: "Practice", desc: "Role-play any scenario", icon: Users },
      { to: "/talk", label: "Talk", desc: "Free-flow voice chat", icon: Mic },
    ],
  },
  reclaim: {
    label: "Reclaim",
    icon: Sparkles,
    match: ["/inner", "/immersions", "/missions", "/training", "/audio", "/resets"],
    items: [
      { to: "/training", label: "The Diet", desc: "7-day natural attraction", icon: Target, admin: true, parked: true },
      { to: "/inner", label: "Inner", desc: "Inner architecture", icon: Sparkles },
      { to: "/audio", label: "Tonight's Audio", desc: "Personalised night audio", icon: Moon },
      { to: "/immersions", label: "Immerse", desc: "Signature immersions", icon: Film },
      { to: "/missions", label: "Missions", desc: "Real-world missions", icon: Target },
      { to: "/resets", label: "Resets", desc: "60-second state shifts", icon: Wind },
    ],
  },
};

const HIDE_ON = ["/", "/login", "/register", "/onboarding", "/topup"];

function SoloTab({ to, label, icon: Icon }) {
  return (
    <NavLink to={to} data-testid={`bottomnav-${label.toLowerCase()}`}
      className={({ isActive }) => `flex flex-col items-center gap-1 flex-1 py-1 rounded-xl transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
      {({ isActive }) => (
        <>
          <span className={`relative flex items-center justify-center w-9 h-9 rounded-full transition-colors ${isActive ? "bg-primary/12" : ""}`}>
            <Icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2.4 : 1.9} />
          </span>
          <span className="text-[10px] tracking-wide">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function GroupTab({ group, testKey }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const active = group.match.some((m) => pathname.startsWith(m));
  const Icon = group.icon;
  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <>
      <button data-testid={`bottomnav-${testKey}`} onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className={`flex flex-col items-center gap-1 flex-1 py-1 rounded-xl transition-colors ${active || open ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
        <span className={`relative flex items-center justify-center w-9 h-9 rounded-full transition-colors ${active || open ? "bg-primary/12" : ""}`}>
          <Icon className="w-[18px] h-[18px]" strokeWidth={active ? 2.4 : 1.9} />
        </span>
        <span className="text-[10px] tracking-wide">{group.label}</span>
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-x-0 top-0 bottom-[calc(64px+env(safe-area-inset-bottom))] z-[45] bg-black/60 backdrop-blur-sm md:hidden" onClick={() => setOpen(false)} data-testid={`bottomnav-${testKey}-backdrop`} />
          <div className="fixed left-1/2 -translate-x-1/2 bottom-[calc(76px+env(safe-area-inset-bottom))] z-[60] w-[min(18rem,calc(100vw-2rem))] p-2 rounded-2xl border border-white/10 bg-[#0d1424] shadow-2xl md:hidden" data-testid={`bottomnav-${testKey}-menu`}>
            {group.items.filter((it) => !it.parked && (!it.admin || user?.is_admin)).map((it) => {
              const isActive = pathname === it.to;
              return (
                <button key={it.to} onClick={() => go(it.to)} data-testid={`bottomnav-${testKey}-${it.label.toLowerCase()}`}
                  className={`w-full flex items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-200 ${isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-white/5"}`}>
                  <span className="mt-0.5 flex items-center justify-center w-8 h-8 rounded-full bg-primary/12 text-primary shrink-0"><it.icon className="w-4 h-4" /></span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium leading-tight">{it.label}</span>
                    <span className="block text-xs text-muted-foreground truncate">{it.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </>, document.body)}
    </>
  );
}

export default function BottomNav() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!user || HIDE_ON.includes(pathname)) return null;

  return (
    <nav className="fixed bottom-0 inset-x-0 z-50 md:hidden border-t border-border bg-background/90 backdrop-blur-xl grain" data-testid="bottom-nav">
      <div className="flex items-stretch justify-around px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <SoloTab {...SOLO[0]} />
        <GroupTab group={GROUPS.coach} testKey="coach" />
        <GroupTab group={GROUPS.reclaim} testKey="reclaim" />
        <SoloTab {...SOLO[1]} />
        <SoloTab {...SOLO[2]} />
      </div>
    </nav>
  );
}
